/**
 * Headless Submit-to-Response for agent auto-apply (suggest_edit).
 * Applies edited client-request OOXML to Word and persists response_submitted.
 */

import type { CrossCheckClause } from '../types/crossCheck';
import apiService from '../services/api';
import { DocumentReader } from '../word/api/DocumentReader';
import { TrackChangesParser } from '../word/utils/TrackChangesParser';
import {
  RedlineInserter,
  type ApplyParagraphResult,
  type SubmitRevertContext,
} from '../word/trackChanges/RedlineInserter';
import { applyViaHybridEngine } from '../features/hybridApply/hybridEngine';
import { RobustXmlTrackChangesGenerator } from '../features/rephrasing/RobustXmlTrackChangesGenerator';
import { isLegacyApplyEnabled } from './addInPreferences';
import { resolveEffectiveParagraphIndex } from './crossCheckDocumentUtils';
import {
  getClientRequestPlainTextForAsk,
  getClientRequestXml,
} from './clauseAskAgentContext';
import { createQrProfiler } from './quickResponseDebug';

export type SubmitClauseEditedXmlResult = {
  applied: boolean;
  revertContext?: SubmitRevertContext;
  error?: string;
};

async function resolveParagraphIndex(
  clause: CrossCheckClause,
  searchOriginal: string,
  clientRequestPlainText: string,
): Promise<number | null> {
  // Same resolution the card's go-to uses: cross-check stores the index on
  // track_changes.paragraph_index for most client-request clauses, not on the clause.
  const paragraphIndex = resolveEffectiveParagraphIndex(clause);
  if (paragraphIndex != null) return paragraphIndex;

  const salesAgreementText = (clause.sales_agreement || '').replace(/\s+/g, ' ').trim();
  const candidates: string[] = [];
  if (salesAgreementText) candidates.push(salesAgreementText);
  const crNorm = clientRequestPlainText.replace(/\s+/g, ' ').trim();
  if (crNorm) candidates.push(crNorm);
  if (searchOriginal && !candidates.includes(searchOriginal)) {
    candidates.push(searchOriginal);
  }

  for (const text of candidates) {
    try {
      const found = await DocumentReader.findParagraphIndexByContent(text);
      if (found != null && found >= 0) return found;
    } catch {
      /* try next */
    }
  }
  return null;
}

/**
 * Apply an edited client-request OOXML to the Word document and mark the clause
 * as response_submitted. Used by agent auto-apply when recommendation is suggest_edit.
 */
export async function submitClauseEditedXml(
  subProjectId: string,
  clause: CrossCheckClause,
  editedXml: string,
  // Passed only by the agent auto-apply Phase C loop, which opens one
  // Word.run() for the whole submit run instead of one per clause — see
  // HybridApplyOptions.sharedContext in hybridEngine.ts for why. Forwarded
  // to applyViaHybridEngine only; the legacy RedlineInserter path (used when
  // isLegacyApplyEnabled()) is unaffected and keeps opening its own context.
  sharedWordContext?: { context: Word.RequestContext; paragraphs: Word.ParagraphCollection },
): Promise<SubmitClauseEditedXmlResult> {
  const profile = createQrProfiler(`agent-auto-apply submit ${clause.id}`);
  if (clause.submission_status?.response_submitted) {
    return { applied: false, error: 'already_submitted' };
  }

  const clientRequestPlainText = getClientRequestPlainTextForAsk(clause);
  const clientRequestXml = getClientRequestXml(clause);
  const salesAgreementText = (clause.sales_agreement || '').replace(/\s+/g, ' ').trim();

  const newText = TrackChangesParser.extractAcceptedText(editedXml).replace(/\s+/g, ' ').trim();
  const trackChangesHtml = TrackChangesParser.convertOoxmlToHtml(editedXml);
  const searchOriginal =
    clientRequestPlainText.replace(/\s+/g, ' ').trim() || salesAgreementText || newText;

  if (!searchOriginal || !newText) {
    return { applied: false, error: 'empty_text' };
  }

  const paragraphIndex = await resolveParagraphIndex(
    clause,
    searchOriginal,
    clientRequestPlainText,
  );
  profile.mark('resolveParagraphIndex', { paragraphIndex });
  if (paragraphIndex == null) {
    return { applied: false, error: 'paragraph_not_found' };
  }

  // Scroll/select the paragraph being applied, so the user can watch each
  // edit land as the run works through the document. When a sharedWordContext
  // is available (the normal Phase C case), select directly against its
  // already-loaded paragraphs collection instead of calling
  // DocumentReader.navigateToParagraph() — that helper opens its own
  // Word.run() and reloads paragraphs.load('items') (the entire paragraph
  // collection) from scratch, which is exactly the redundant per-clause cost
  // Phase C's shared context exists to avoid. Falls back to the standalone
  // helper when there's no shared context (e.g. the setup-failure fallback
  // path in agentAutoApply.ts).
  try {
    if (sharedWordContext) {
      const rawParagraph = sharedWordContext.paragraphs.items[Number(paragraphIndex)];
      if (rawParagraph) {
        (rawParagraph as unknown as Word.Paragraph).select();
        await sharedWordContext.context.sync();
      }
    } else {
      await DocumentReader.navigateToParagraph(Number(paragraphIndex));
    }
  } catch {
    /* navigation/selection failure is non-fatal for apply */
  }

  const useIncremental =
    trackChangesHtml.length > 0 &&
    (trackChangesHtml.includes('track-change-insertion') ||
      trackChangesHtml.includes('track-change-deletion'));

  let applyResult: ApplyParagraphResult = { applied: false };
  const useHybridApply = !isLegacyApplyEnabled();

  if (useHybridApply) {
    const hybrid = await applyViaHybridEngine(Number(paragraphIndex), newText, {
      ...(sharedWordContext ? { sharedContext: sharedWordContext } : {}),
    });
    applyResult = { applied: hybrid.applied, revertContext: hybrid.revertContext };
    profile.mark('applyViaHybridEngine', { applied: hybrid.applied, syncCount: hybrid.trace.syncCount });
  } else {
    applyResult = await RedlineInserter.applyParagraphFromEditedOoxml(editedXml, paragraphIndex, {
      searchText: searchOriginal,
    });

    if (!applyResult.applied) {
      const regenBaseline =
        clientRequestXml ||
        TrackChangesParser.createMinimalParagraphOoxml(clientRequestPlainText);
      const regenerated = RobustXmlTrackChangesGenerator.generateTrackChangesXml(
        regenBaseline,
        newText,
        'AI Suggestion',
        { source: 'agent-auto-apply', clauseId: clause.id },
        'char',
        { mode: 'simple-edit', simpleEditDiffStrategy: 'contiguous' },
      );
      applyResult = await RedlineInserter.applyParagraphFromEditedOoxml(
        regenerated,
        paragraphIndex,
        { searchText: searchOriginal },
      );
    }

    if (!applyResult.applied && useIncremental) {
      applyResult = await RedlineInserter.applyWordLevelChangesIncrementally(
        searchOriginal,
        newText,
        trackChangesHtml,
        paragraphIndex,
      );
    }
    profile.mark('legacy RedlineInserter apply', { applied: applyResult.applied });
  }

  if (!applyResult.applied) {
    return { applied: false, error: 'apply_failed' };
  }

  try {
    await apiService.updateClauseResponseSubmissionStatus(
      subProjectId,
      clause.id,
      true,
      applyResult.revertContext,
    );
    profile.mark('backend updateClauseResponseSubmissionStatus');
  } catch (backendErr) {
    profile.mark('backend updateClauseResponseSubmissionStatus FAILED');
    console.warn(
      '[submitClauseEditedXml] Backend submit status update failed (Word changes still applied):',
      backendErr,
    );
  }

  return {
    applied: true,
    revertContext: applyResult.revertContext,
  };
}
